// The rich-text contract shared by every renderer (site, Unity, mail): a small markdown subset plus three
// semantic marks. Nothing here knows colours - a renderer maps the marks to its palette.
//
//   # Title            centred title between rules        ## Header      section header
//   **bold** *italic*  - item / 1. item (nest by two spaces)   ---        rule
//   {+ text +} positive   {- text -} negative   {! text !} highlight     single newline = line break
//
// parse(md) -> ProseMirror JSON (the editor's document), serialize(doc) -> md, toHtml(md), plainText(md).
// Own parser on purpose: the grammar is tiny and the round trip (serialize(parse(md)) == md) is what keeps the
// stored text stable when an item is opened and saved untouched.

export const SEMANTIC_KINDS = ["positive", "negative", "highlight"];
const OPEN = { "+": "positive", "-": "negative", "!": "highlight" };
const CLOSE = { positive: "+", negative: "-", highlight: "!" };

// ---------- inline ----------
// Tokens: text, bold, italic, semantic; nesting allowed. Returns ProseMirror inline nodes.
function parseInline(text) {
    const nodes = [];
    const stack = []; // active marks in order: { type, kind }
    let buf = "";

    const flush = () => {
        if (!buf) return;
        const marks = stack.map(m => m.type === "semantic" ? { type: "semantic", attrs: { kind: m.kind } } : { type: m.type });
        nodes.push(marks.length ? { type: "text", text: buf, marks } : { type: "text", text: buf });
        buf = "";
    };
    const has = (type, kind) => stack.some(m => m.type === type && (kind == null || m.kind === kind));
    const toggle = (type, kind) => {
        flush();
        const i = stack.findIndex(m => m.type === type && (kind == null || m.kind === kind));
        if (i >= 0) stack.splice(i, 1); else stack.push({ type, kind });
    };

    let i = 0;
    while (i < text.length) {
        const c = text[i], n = text[i + 1];
        if (c === "\\" && n != null) { buf += n; i += 2; continue; }
        if (c === "*" && n === "*") { toggle("bold"); i += 2; continue; }
        if (c === "*") {
            // italic opens before a non-space and closes after a non-space
            if (has("italic")) { if (i > 0 && text[i - 1] !== " ") { toggle("italic"); i++; continue; } }
            else if (n && n !== " ") { toggle("italic"); i++; continue; }
        }
        if (c === "{" && OPEN[n] && text[i + 2] === " " && !has("semantic", OPEN[n])) { toggle("semantic", OPEN[n]); i += 3; continue; }
        if (c === " " && CLOSE[stack.find(m => m.type === "semantic")?.kind] === n && text[i + 2] === "}" && has("semantic")) {
            const open = [...stack].reverse().find(m => m.type === "semantic");
            if (open && CLOSE[open.kind] === n) { toggle("semantic", open.kind); i += 3; continue; }
        }
        buf += c;
        i++;
    }
    flush();
    return nodes;
}

// ---------- blocks ----------
const LIST_RE = /^(\s*)(?:([-*])|(\d+)\.)\s+(.*)$/;

export function parse(md) {
    const lines = (md ?? "").replace(/\r\n?/g, "\n").split("\n");
    const doc = { type: "doc", content: [] };
    let para = null;          // lines of the paragraph being built
    let list = null;          // { stack: [{ node, indent }] }

    const flushPara = () => {
        if (!para) return;
        const content = [];
        para.forEach((line, idx) => {
            if (idx > 0) content.push({ type: "hardBreak" });
            content.push(...parseInline(line));
        });
        doc.content.push(content.length ? { type: "paragraph", content } : { type: "paragraph" });
        para = null;
    };
    const flushList = () => { list = null; };

    for (const raw of lines) {
        const line = raw.replace(/\s+$/, "");
        const m = line.match(LIST_RE);
        if (m) {
            flushPara();
            const indent = Math.floor(m[1].replace(/\t/g, "  ").length / 2);
            const ordered = m[3] != null;
            const item = { type: "listItem", content: [{ type: "paragraph", content: parseInline(m[4]) }] };
            if (!list) list = { stack: [] };
            // pop deeper levels
            while (list.stack.length && list.stack[list.stack.length - 1].indent > indent) list.stack.pop();
            const top = list.stack[list.stack.length - 1];
            if (!top || top.indent < indent) {
                const node = { type: ordered ? "orderedList" : "bulletList", content: [item] };
                if (top) {
                    const parentItem = top.node.content[top.node.content.length - 1];
                    parentItem.content.push(node);
                } else doc.content.push(node);
                list.stack.push({ node, indent });
            } else {
                // same level: a kind change starts a sibling list
                if ((top.node.type === "orderedList") !== ordered) {
                    list.stack.pop();
                    const node = { type: ordered ? "orderedList" : "bulletList", content: [item] };
                    const parent = list.stack[list.stack.length - 1];
                    if (parent) parent.node.content[parent.node.content.length - 1].content.push(node); else doc.content.push(node);
                    list.stack.push({ node, indent });
                } else top.node.content.push(item);
            }
            continue;
        }
        flushList();
        if (line.trim() === "") { flushPara(); continue; }
        if (/^---+\s*$/.test(line)) { flushPara(); doc.content.push({ type: "horizontalRule" }); continue; }
        const h = line.match(/^(#{1,2})\s+(.*)$/);
        if (h) { flushPara(); doc.content.push({ type: "heading", attrs: { level: h[1].length }, content: parseInline(h[2]) }); continue; }
        if (!para) para = [];
        // a leading backslash escapes a block marker; inline escapes are parseInline's
        para.push(line.replace(/^\\(?=(#{1,2} |[-*] |\d+\. |---))/, ""));
    }
    flushPara();
    return doc;
}

// ---------- serialize ----------
function escapeText(t) {
    return t.replace(/\\/g, "\\\\").replace(/\*/g, "\\*").replace(/\{([+\-!]) /g, "\\{$1 ");
}

function serializeInline(nodes) {
    let out = "";
    let active = []; // marks currently open, in order
    const key = m => m.type === "semantic" ? `semantic:${m.attrs?.kind}` : m.type;
    const open = m => m.type === "bold" ? "**" : m.type === "italic" ? "*" : `{${CLOSE[m.attrs?.kind] ?? "!"} `;
    const close = m => m.type === "bold" ? "**" : m.type === "italic" ? "*" : ` ${CLOSE[m.attrs?.kind] ?? "!"}}`;
    for (const n of nodes ?? []) {
        if (n.type === "hardBreak") {
            // marks never span lines in the text form: close everything, the next node reopens what it carries
            for (const m of [...active].reverse()) out += close(m);
            active = [];
            out += "\n";
            continue;
        }
        const marks = (n.marks ?? []).filter(m => ["bold", "italic", "semantic"].includes(m.type));
        // close marks no longer present (from the innermost), open the new ones
        let common = 0;
        while (common < active.length && common < marks.length && key(active[common]) === key(marks[common])) common++;
        for (const m of active.slice(common).reverse()) out += close(m);
        active = active.slice(0, common);
        for (const m of marks.slice(common)) { out += open(m); active.push(m); }
        out += escapeText(n.text ?? "");
    }
    for (const m of [...active].reverse()) out += close(m);
    return out;
}

function serializeList(node, depth, lines) {
    const ordered = node.type === "orderedList";
    let n = 1;
    for (const item of node.content ?? []) {
        const marker = ordered ? `${n++}.` : "-";
        let first = true;
        for (const child of item.content ?? []) {
            if (child.type === "paragraph") {
                const text = serializeInline(child.content).split("\n");
                const isFirst = first;
                text.forEach((t, i) => lines.push("  ".repeat(depth) + (isFirst && i === 0 ? `${marker} ` : "  ") + t));
                first = false;
            } else if (child.type === "bulletList" || child.type === "orderedList") serializeList(child, depth + 1, lines);
        }
        if (first) lines.push("  ".repeat(depth) + marker + " ");
    }
}

export function serialize(doc) {
    const blocks = [];
    for (const node of doc?.content ?? []) {
        switch (node.type) {
            case "heading": blocks.push(`${"#".repeat(Math.min(2, node.attrs?.level ?? 2))} ${serializeInline(node.content)}`); break;
            case "horizontalRule": blocks.push("---"); break;
            case "bulletList":
            case "orderedList": { const lines = []; serializeList(node, 0, lines); blocks.push(lines.join("\n")); break; }
            default: { // paragraph
                const text = serializeInline(node.content);
                blocks.push(text.replace(/^(#{1,2} |---\s*$|[-*] |\d+\. )/gm, "\\$1"));
            }
        }
    }
    return blocks.join("\n\n");
}

// ---------- html ----------
const esc = s => String(s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");

function inlineHtml(nodes) {
    return (nodes ?? []).map(n => {
        if (n.type === "hardBreak") return "<br>";
        let html = esc(n.text ?? "");
        for (const m of [...(n.marks ?? [])].reverse()) {
            if (m.type === "bold") html = `<strong>${html}</strong>`;
            else if (m.type === "italic") html = `<em>${html}</em>`;
            else if (m.type === "semantic") html = `<span class="rt-${esc(m.attrs?.kind ?? "highlight")}">${html}</span>`;
        }
        return html;
    }).join("");
}

function blockHtml(node) {
    switch (node.type) {
        case "heading": return `<h${node.attrs?.level === 1 ? 1 : 2} class="rt-h${node.attrs?.level === 1 ? 1 : 2}">${inlineHtml(node.content)}</h${node.attrs?.level === 1 ? 1 : 2}>`;
        case "horizontalRule": return `<hr class="rt-hr">`;
        case "bulletList": return `<ul class="rt-list">${(node.content ?? []).map(blockHtml).join("")}</ul>`;
        case "orderedList": return `<ol class="rt-list">${(node.content ?? []).map(blockHtml).join("")}</ol>`;
        case "listItem": return `<li>${(node.content ?? []).map(c => c.type === "paragraph" ? inlineHtml(c.content) : blockHtml(c)).join("")}</li>`;
        default: return `<p class="rt-p">${inlineHtml(node.content)}</p>`;
    }
}

export function toHtml(md) {
    return (parse(md).content ?? []).map(blockHtml).join("");
}

export function plainText(md, max) {
    const walk = nodes => (nodes ?? []).map(n => n.type === "text" ? n.text : n.type === "hardBreak" ? " " : (n.content ? walk(n.content) + " " : "")).join("");
    const text = walk(parse(md).content).replace(/\s+/g, " ").trim();
    return max && text.length > max ? text.slice(0, max - 1) + "…" : text;
}
